import { useState, useRef } from 'react';
import { useSelector } from 'react-redux';

import { Modal, Box, Button, CircularProgress, Divider, Alert, Stack } from '@mui/material';

import StyledWrapper from './style';
import AppProcessWarnings from '../../components/AppProcessWarnings/AppProcessWarnings';
import Table from '../Table/Table';
import BulkRevToEvalColumns from './BulkRevToEvalColumns';
import LinkReviewIcon from '../../assets/customIcons/linkReviewIcon';
import ReviewFormIcon from '../../assets/customIcons/reviewForm';
import PersonIcon from '../../assets/customIcons/personIcon';
import useGetAppToRevTable from '../../hooks/useGetAppToRevTable';
import { useBulkAssignReviewMutation } from '../../services/endpoints/reviewEndpoints';
import { getLocalAuth } from '../../utils/environmentUtils';

const style = {
	position: 'absolute',
	top: '50%',
	left: '50%',
	transform: 'translate(-50%, -50%)',
	width: '90%',
	bgcolor: 'background.paper',
	borderRadius: '4px',
	boxShadow:
		'0px 11px 15px -7px rgba(0, 0, 0, 0.2), 0px 24px 38px 3px rgba(0, 0, 0, 0.14), 0px 9px 46px 8px rgba(0, 0, 0, 0.12)',
	p: 4
};

const BulkRevToEvalModal = ({
	openModal,
	closeModal,
	chosenReviews,
	handleAlert,
	outerTableRef
}) => {
	const accountSource = useSelector((state) => state?.login?.accountSource);
	const apiToken = getLocalAuth(accountSource)?.access_token;
	const [bulkAssign] = useBulkAssignReviewMutation();

	const [open, setOpen] = useState(!!openModal);
	const [columnWidth, setColumnWidth] = useState({});
	const [isLoading, setIsLoading] = useState(false);
	const [chosenEvalStages, setChosenEvalStages] = useState([]);

	const tableRef = useRef(null);

	const initialColumns = BulkRevToEvalColumns(columnWidth);
	const { data, pagination } = useGetAppToRevTable();

	const hasAssignable = pagination.isUninitialized || pagination.loading || pagination.rowCount > 0;

	const handleClose = () => {
		outerTableRef?.current?.clearSelection();
		closeModal && closeModal();
		setOpen(false);
	};

	const getPageTitle = () => (
		<div style={{ display: 'flex', alignItems: 'center', gap: '6px' }}>
			<div style={{ marginTop: '-22px' }}>
				<PersonIcon width={23} />
			</div>
			{`Bulk assign the selected reviewers:`} <br />
			{chosenReviews?.map((e, index) => (
				<>
					{`${e?.first_name} ${e?.last_name}`}
					{index !== chosenReviews?.length - 1 && ', '}
				</>
			))}
		</div>
	);

	const handleSubmit = async () => {
		setIsLoading(true);

		const payload = {
			reviewers: chosenReviews.map((e) => e.reviewer_id),
			stages: chosenEvalStages.map((e) => e.stageId?.replace('esi-', ''))
		};

		const options = {
			payload,
			apiToken
		};

		const reviewers = chosenReviews
			.map((reviewer) => `${reviewer.first_name} ${reviewer.last_name}`)
			.join(', ');
		const applications = chosenEvalStages.map((application) => application.stageName).join(', ');

		await bulkAssign(options)
			.unwrap()
			.then(() =>
				handleAlert(
					`${reviewers} successfully assigned to review ${applications}.`,
					'success',
					false
				)
			)
			.catch(() =>
				handleAlert(
					"The assignment didn't finalize due to a system error. Please try again or contact support.",
					'error'
				)
			);

		setIsLoading(false);
		handleClose();
	};

	return (
		<div>
			{!isLoading && (
				<Modal
					open={open}
					onClose={handleClose}
					aria-labelledby="modal-modal-title"
					aria-describedby="modal-modal-description"
					sx={{ maxHeight: '90vh' }}
				>
					<StyledWrapper.TableContainer hideDropdownCarot>
						<Box sx={style}>
							<div style={{ paddingLeft: '20px', paddingRight: '20px' }}>
								<StyledWrapper.Title>{getPageTitle()}</StyledWrapper.Title>
								{hasAssignable && <AppProcessWarnings assigned={true} />}
							</div>
							<Divider variant={'middle'} sx={{ '&::before, &::after': { borderTopWidth: '5px' } }}>
								Assign to
							</Divider>

							{!hasAssignable && (
								<Stack
									sx={{ height: '60vh', width: '100%', marginBottom: '20px', padding: '20px' }}
									spacing={2}
								>
									<Alert variant="outlined" severity="info">
										There are no applications in a stage that can be assigned at this time.
									</Alert>
								</Stack>
							)}

							{hasAssignable && (
								<Table
									customHeight={'50vh'}
									columns={initialColumns}
									checkboxSelection
									rowHeight={52}
									enableAutoPageSize
									rows={data || []}
									isRowSelectable={(data) =>
										data.row.stageId &&
										(data.row.appStatus.includes('In Progress') ||
											data.row.appStatus === 'Not Started')
									}
									keepNonExistentRowsSelected
									rowCount={pagination?.rowCount}
									pagination={pagination?.pagination}
									pageSize={pagination?.pageSize}
									paginationMode={pagination?.paginationMode}
									onPageChange={(e) => pagination?.onPageChange(e)}
									onPageSizeChange={(e) => pagination?.onPageSizeChange(e)}
									page={pagination?.page}
									loading={pagination?.loading}
									sortingMode={pagination?.sortingMode}
									onSortModelChange={(e) => pagination?.onSortModelChange(e)}
									onFilterModelChange={(e) => pagination?.onFilterModelChange(e)}
									onSelectChange={(e) => setChosenEvalStages(e)}
									disableSelectionOnClick
									key={'BulkRevToEvalModal'}
									onColumnResize={(e) => {
										const newColumnSizes = e?.colDef?.field;
										const width = e?.width;
										setColumnWidth({ ...columnWidth, [newColumnSizes]: width });
									}}
									filterModel={pagination?.filterModel}
									sortModel={pagination?.sortModel}
									hideTableActions={true}
									isOpenedFilter={true}
									ref={tableRef}
								/>
							)}

							<StyledWrapper.ButtonContainer>
								<Button onClick={handleClose} variant="outlined" size="small">
									CANCEL
								</Button>
								<Button
									disabled={chosenEvalStages?.length === 0}
									onClick={handleSubmit}
									variant="contained"
									size="large"
									style={{
										display: 'flex',
										alignItems: 'center',
										gap: '10px'
									}}
								>
									{'ASSIGN'}
									<div style={{ height: '30px' }}>
										<LinkReviewIcon isActive={chosenEvalStages?.length} color={'white'} />
										<ReviewFormIcon
											isActive={chosenEvalStages?.length}
											isBlueprint
											status={'White'}
											customDisabledColor={'rgba(0, 0, 0, 0.26)'}
										/>
									</div>
								</Button>
							</StyledWrapper.ButtonContainer>
						</Box>
					</StyledWrapper.TableContainer>
				</Modal>
			)}

			{isLoading && (
				<Modal
					disableBackdropClick
					open={open}
					onClose={(e, reason) => {
						if (reason !== 'backdropClick') {
							handleClose();
						}
					}}
					aria-labelledby="modal-modal-title"
					aria-describedby="modal-modal-description"
				>
					<Box sx={{ ...style, maxWidth: '412px' }}>
						<StyledWrapper.Title>Processing...</StyledWrapper.Title>
						<StyledWrapper.Description center>
							<CircularProgress sx={{ margin: '50px 0px' }} />
						</StyledWrapper.Description>
					</Box>
				</Modal>
			)}
		</div>
	);
};

export default BulkRevToEvalModal;
